import { type EmbedTarget, type EmbedTargetType } from './embed-target'
import { type MediaControls } from '../media'
import { type EmbedCSSStyles } from '../style'
import { type EmbedElementContext } from '../element-embedder'
import { type EmbedStrategy } from '../embed-strategies/embed-strategy'
import { IFrameEmbedStrategy, type IFrameEmbedHandler } from '../embed-strategies/iframe/IFrameEmbedStrategy'
import { logger } from '../../utils/logger'

export abstract class BaseEmbedTarget implements EmbedTarget {
  ctx: EmbedElementContext
  readonly name: EmbedTargetType
  defaultIframeStyle?: EmbedCSSStyles
  abstract mediaControl?: MediaControls | undefined
  embedStrategy: EmbedStrategy

  constructor (name: EmbedTargetType, ctx: EmbedElementContext) {
    this.name = name
    this.ctx = ctx
  }

  #getEmbedStrategy (): EmbedStrategy {
    const factory = this.ctx.embedStrategyFactoryRegistry.get(this.ctx.settings.strategy)
    if (factory != null) return factory.create(this)
    // TODO enhance this by implementing supports() method in EmbedStrategy
    if (this.ctx.settings.strategy != null && !['parent', 'float-absolute', 'float-fixed'].includes(this.ctx.settings.strategy)) {
      logger.warn(`EmbedStrategy for ${this.ctx.settings.strategy} could not be found. Falling back to the default iframe strategy. Perhaps you forgot to register EmbedStrategyFactory?`)
    }
    return this.#getDefaultEmbedStrategy()
  }

  #getDefaultEmbedStrategy (): EmbedStrategy {
    return new IFrameEmbedStrategy(this, this.getIFrameEmbedHandler())
  }

  async embed (): Promise<void> {
    if (this.embedStrategy == null) {
      this.embedStrategy = this.#getEmbedStrategy()
      await this.embedStrategy.embed()
    } else {
      logger.warn('EmbedTarget is already embedded.')
    }
  }

  protected abstract getIFrameEmbedHandler (): IFrameEmbedHandler

  abstract setPlaysInline (value: boolean): void

  get bottomSpace (): string {
    return this.ctx.settings.bottomSpace ?? '20%'
  }
}
